<template>
  <div>
    <b-overlay :show="loading" rounded="sm" opacity="0.6" no-fade>
      <b-card class="card_body_bottom">
        <template #header>
          <b-row class="flex-row justify-content-start align-items-center" style="width: 100%">
            <b-col cols="12" xl="6" lg="6" md="6" sm="12" class="d-flex justify-content-start align-items-center" style="gap:5px">
              <h4 class="mb-0">Azimut Reports</h4>
              <b-button
                variant="flat-secondary"
                class="btn-icon rounded-circle"
                :disabled="selectedAccounts.length === 0"
                @click="fetchData"
                v-b-tooltip.hover="'Refresh Report'"
              >
                <feather-icon icon="RefreshCcwIcon" />
              </b-button>
            </b-col>
          </b-row>
        </template>

        <b-card-body>
          <b-row class="align-items-end">
            <!-- Accounts -->
            <b-col cols="12" md="6" xl="4" lg="4" sm="12">
              <label class="compact_label">Accounts</label>
              <v-select
                class="compact_form_vue-multi-select"
                v-model="selectedAccounts"
                label="AccountName"
                :options="optAccounts"
                multiple
                :close-on-select="false"
                placeholder="Select account(s)"
              />
            </b-col>

            <!-- Select all accounts -->
            <b-col cols="12" md="6" xl="2" lg="2" sm="12" style="padding-bottom: 0.4rem;">
              <b-form-checkbox v-model="allAccountsSelected" @change="toggleAllAccounts">
                All Accounts
              </b-form-checkbox>
            </b-col>

            <!-- Start Date -->
            <b-col cols="12" md="6" xl="2" lg="2" sm="12">
              <label class="compact_label">Start Date</label>
              <b-form-datepicker
                v-model="startDate"
                placeholder="Start date"
                :start-weekday="1"
                :hide-header="true"
                class="form-control"
                today-button
                reset-button
              />
            </b-col>

            <!-- End Date -->
            <b-col cols="12" md="6" xl="2" lg="2" sm="12">
              <label class="compact_label">End Date</label>
              <b-form-datepicker
                v-model="endDate"
                placeholder="End date"
                :start-weekday="1"
                :hide-header="true"
                :min="startDate"
                class="form-control"
                today-button
                reset-button
              />
            </b-col>
          </b-row>

          <b-row class="mt-1">
            <b-col cols="12" class="d-flex align-items-end" style="gap: 8px;">
              <b-button
                v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                variant="primary"
                size="sm"
                :disabled="selectedAccounts.length === 0"
                @click="fetchData"
              >
                <feather-icon icon="SearchIcon" class="mr-50" />
                <span class="align-middle">Generate Reports</span>
              </b-button>

              <b-button
                v-if="tradeRows.length > 0"
                v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                variant="outline-success"
                size="sm"
                :disabled="downloading"
                @click="downloadBoth"
              >
                <b-spinner v-if="downloading" small class="mr-50" />
                <feather-icon v-else icon="DownloadIcon" class="mr-50" />
                <span class="align-middle">Download Both XLSX</span>
              </b-button>
            </b-col>
          </b-row>
        </b-card-body>
      </b-card>

      <template #overlay v-if="loading">
        <div class="text-center d-flex justify-content-center align-items-start mt-3">
          <b-icon icon="stopwatch" font-scale="3" animation="cylon" />
          <p id="cancel-label">
            <feather-icon icon="TargetIcon" />
            Preparing reports...
          </p>
        </div>
      </template>
    </b-overlay>

    <!-- Trades Report -->
    <b-card v-if="tradeRows.length > 0" class="mt-1">
      <div class="d-flex justify-content-between align-items-center mb-1">
        <h5 class="mb-0">Trades ({{ fileStem }}.xlsx)</h5>
        <span class="text-muted" style="font-size: 0.8rem;">
          {{ tradeRows.length }} trade(s)
        </span>
      </div>
      <div class="table-responsive">
        <b-table
          class="compact_table azimut-report-table"
          :items="tradeRows"
          :fields="tableColumns(tradeRows)"
          :small="true"
          :hover="true"
          :striped="true"
          :bordered="true"
          :fixed="false"
          headVariant="dark"
          table-variant="none"
          responsive
        />
      </div>
    </b-card>

    <!-- Settlement (Azimut) Report -->
    <b-card v-if="settlementRows.length > 0" class="mt-1">
      <div class="d-flex justify-content-between align-items-center mb-1">
        <h5 class="mb-0">Azimut (Azimut{{ fileStem }}.xlsx)</h5>
        <span class="text-muted" style="font-size: 0.75rem;">Scroll horizontally to view all columns</span>
      </div>
      <div class="table-responsive">
        <b-table
          class="compact_table azimut-report-table"
          :items="settlementRows"
          :fields="tableColumns(settlementRows)"
          :small="true"
          :hover="true"
          :striped="true"
          :bordered="true"
          :fixed="false"
          headVariant="dark"
          table-variant="none"
          responsive
        />
      </div>
    </b-card>

    <b-alert v-if="searched && tradeRows.length === 0" variant="primary" show class="mt-1">
      <div class="alert-body">
        <span>No trades found for the selected criteria.</span>
      </div>
    </b-alert>
  </div>
</template>

<script>
import {
  BCard, BCardBody, BRow, BCol, BButton, BFormDatepicker, BFormCheckbox,
  BTable, BOverlay, BIcon, BAlert, BSpinner, VBTooltip,
} from 'bootstrap-vue';
import store from '@/store';
import { onUnmounted } from '@vue/composition-api';
import vSelect from 'vue-select';
import azimutReportStoreModule from './azimutReportStoreModule';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import Ripple from 'vue-ripple-directive';

const STORE_MODULE = 'azimutReport';

export default {
  components: {
    BCard, BCardBody, BRow, BCol, BButton, BFormDatepicker, BFormCheckbox,
    BTable, BOverlay, BIcon, BAlert, BSpinner, vSelect, ToastificationContent,
  },

  directives: {
    'b-tooltip': VBTooltip,
    Ripple,
  },

  setup() {
    if (!store.hasModule(STORE_MODULE)) {
      store.registerModule(STORE_MODULE, azimutReportStoreModule);
    }
    onUnmounted(() => {
      if (store.hasModule(STORE_MODULE)) store.unregisterModule(STORE_MODULE);
    });
  },

  data() {
    return {
      selectedAccounts: [],
      optAccounts: [],
      allAccountsSelected: false,
      startDate: null,
      endDate: null,
      tradeRows: [],
      settlementRows: [],
      loading: false,
      downloading: false,
      searched: false,
    };
  },

  computed: {
    queryParams() {
      return {
        accounts: this.selectedAccounts.map((a) => a.AccountName),
        dateStart: this.startDate,
        dateEnd: this.endDate,
      };
    },

    // "<Account or ALL>-<mm-dd-yy>" (US style) matching the backend file names
    fileStem() {
      const account =
        this.selectedAccounts.length === 1
          ? this.selectedAccounts[0].AccountName
          : 'ALL';
      const d = this.endDate ? new Date(this.endDate) : new Date();
      const mdy = `${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}-${String(d.getFullYear()).slice(-2)}`;
      return `${account}-${mdy}`;
    },
  },

  mounted() {
    this.getAccounts();
  },

  methods: {
    tableColumns(rows) {
      if (rows.length === 0) return [];
      return Object.keys(rows[0]).map((key) => ({
        key,
        label: key,
        thStyle: { minWidth: '110px' },
      }));
    },

    getAccounts() {
      store
        .dispatch('azimutReport/getAzimutAccounts')
        .then((res) => {
          this.optAccounts = res.data.resData || [];
        })
        .catch((e) => {
          this.showError(`Error fetching accounts: ${e.message}`);
        });
    },

    toggleAllAccounts(checked) {
      this.selectedAccounts = checked ? [...this.optAccounts] : [];
    },

    fetchData() {
      if (this.selectedAccounts.length === 0) return;
      this.loading = true;
      this.searched = false;

      store
        .dispatch('azimutReport/getAzimutReportData', this.queryParams)
        .then((res) => {
          this.tradeRows = res.data.trades || [];
          this.settlementRows = res.data.settlement || [];
          this.searched = true;
          this.loading = false;
        })
        .catch((e) => {
          this.loading = false;
          this.searched = true;
          this.showError(`Error generating reports: ${e.message}`);
        });
    },

    saveBlob(blob, fileName) {
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },

    async downloadBoth() {
      if (this.selectedAccounts.length === 0) return;
      this.downloading = true;

      try {
        const [trades, settlement] = await Promise.all([
          store.dispatch('azimutReport/downloadAzimutTradesXlsx', this.queryParams),
          store.dispatch('azimutReport/downloadAzimutSettlementXlsx', this.queryParams),
        ]);

        this.saveBlob(trades.data, `${this.fileStem}.xlsx`);
        this.saveBlob(settlement.data, `Azimut${this.fileStem}.xlsx`);
      } catch (e) {
        this.showError(`Error downloading reports: ${e.message}`);
      }

      this.downloading = false;
    },

    showError(msg) {
      this.$toast({
        component: ToastificationContent,
        props: { title: 'Error', text: msg, icon: 'AlertTriangleIcon', variant: 'danger' },
      });
    },
  },

  watch: {
    selectedAccounts(val) {
      this.allAccountsSelected = this.optAccounts.length > 0 && val.length === this.optAccounts.length;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~/src/assets/scss/compact-design.scss";

.azimut-report-table {
  font-size: 0.72rem;
}

.card::v-deep .card-body {
  padding: 0.8rem 0.7rem !important;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
